import { useEffect, useRef } from "react";
import { ethers } from "ethers";
import { EVM_CONFIG } from "../../config";
import { executeEvmConsolidation } from "../../joinsplit-v2/consolidation";
import { usesPrivateFees } from "../../joinsplit-v2/privateFees";
import {
  hasActiveFlatJoinSplitV2Operation,
  withFlatJoinSplitV2WalletLock,
} from "../../joinsplit-v2/walletLock";
import { getBaseReadProvider } from "../../utils/base-read-provider";
import { startRecoveryPolling } from "../helpers/recoveryPolling";
import type { BaseStoredNote, BaseWalletState, BaseLogType } from "../types";

/** Four bounded attempts per connection; fresh notes can trigger the next pair. */
export function useBaseConsolidation(params: {
  wallet: BaseWalletState;
  enabled: boolean;
  notes: BaseStoredNote[];
  setNotes: React.Dispatch<React.SetStateAction<BaseStoredNote[]>>;
  isPaused: () => boolean;
  addLog: (message: string, type?: BaseLogType, txHash?: string) => void;
}) {
  const latest = useRef(params);
  latest.current = params;
  const budget = useRef({
    keys: params.wallet.spendingKeys,
    account: params.wallet.address,
    attempts: 0,
    lastCandidates: "",
  });
  const pool = EVM_CONFIG.CONTRACTS.SHIELDED_POOL_JOINSPLIT;
  useEffect(() => {
    if (
      !params.enabled ||
      !params.wallet.spendingKeys ||
      !params.wallet.signer ||
      !EVM_CONFIG.JOINSPLIT_V2.ENABLED ||
      usesPrivateFees(pool)
    )
      return;
    let disposed = false;
    if (
      budget.current.keys !== params.wallet.spendingKeys ||
      budget.current.account !== params.wallet.address
    ) {
      budget.current = {
        keys: params.wallet.spendingKeys,
        account: params.wallet.address,
        attempts: 0,
        lastCandidates: "",
      };
    }
    const connection = budget.current;
    let active: AbortController | null = null;
    const account = params.wallet.address;
    const keys = params.wallet.spendingKeys;
    const signer = params.wallet.signer as ethers.Signer;
    const pause = () =>
      document.hidden ||
      latest.current.isPaused() ||
      hasActiveFlatJoinSplitV2Operation();
    const stop = startRecoveryPolling({
      runImmediately: false,
      intervalMs: 30_000,
      isPaused: pause,
      run: async () => {
        if (connection.attempts >= 4 || disposed) return;
        const candidates = latest.current.notes.filter(
          ({ note, spent }) =>
            !spent &&
            note.protocol === "flat-joinsplit-v2" &&
            note.schemaVersion === 2 &&
            note.chainId === EVM_CONFIG.CHAIN_ID &&
            note.poolAddress?.toLowerCase() === pool.toLowerCase() &&
            note.leafIndex >= 0,
        );
        const key = candidates
          .map(({ note }) => note.commitment.toLowerCase())
          .sort()
          .join(",");
        if (candidates.length < 2 || key === connection.lastCandidates) return;
        const groups = new Map<string, number>();
        let token: string | undefined;
        for (const { note } of candidates) {
          if (!ethers.isAddress(note.tokenData?.tokenAddress || "")) continue;
          const address = ethers.getAddress(note.tokenData.tokenAddress);
          const group = `${note.treeIndex}:${note.tokenHash}:${address}`;
          const count = (groups.get(group) ?? 0) + 1;
          groups.set(group, count);
          if (count >= 2) {
            token = address;
            break;
          }
        }
        if (!token) return;
        connection.attempts++;
        connection.lastCandidates = key;
        const controller = new AbortController();
        active = controller;
        const timeout = setTimeout(() => controller.abort(), 120_000);
        try {
          const result = await withFlatJoinSplitV2WalletLock({
            chainId: EVM_CONFIG.CHAIN_ID,
            poolAddress: pool,
            accountAddress: account,
            priority: "background",
            signal: controller.signal,
            run: (signal) =>
              executeEvmConsolidation({
                signer,
                provider: getBaseReadProvider(),
                chainId: BigInt(EVM_CONFIG.CHAIN_ID),
                pool,
                token: token!,
                notes: latest.current.notes,
                spendingKeys: keys,
                signal,
                onLog: () => {},
              }),
          });
          if (
            !result ||
            disposed ||
            latest.current.wallet.spendingKeys !== keys ||
            latest.current.wallet.address !== account
          )
            return;
          const spent = new Set(
            result.spentCommitments.map((value) => value.toLowerCase()),
          );
          latest.current.setNotes((current) => {
            if (
              disposed ||
              latest.current.wallet.spendingKeys !== keys ||
              latest.current.wallet.address !== account
            )
              return current;
            const updated = current.map((stored) =>
              spent.has(stored.note.commitment.toLowerCase())
                ? { ...stored, spent: true }
                : stored,
            );
            const existing = new Set(
              updated.map(({ note }) => note.commitment.toLowerCase()),
            );
            return [
              ...updated,
              ...result.outputs
                .filter((note) => !existing.has(note.commitment.toLowerCase()))
                .map((note) => ({
                  note,
                  spent: false,
                  timestamp: Date.now(),
                  txHash: result.txHash,
                })),
            ];
          });
          latest.current.addLog(
            "Combined two private notes with sponsored gas",
            "success",
            result.txHash,
          );
        } catch (error) {
          if (!disposed && !controller.signal.aborted)
            latest.current.addLog(
              "Automatic note consolidation paused. Pending attempts remain available in recovery.",
              "info",
            );
        } finally {
          clearTimeout(timeout);
          if (active === controller) active = null;
        }
      },
      onError: () => {},
    });
    const yieldToInteraction = () => active?.abort();
    document.addEventListener("pointerdown", yieldToInteraction, true);
    document.addEventListener("keydown", yieldToInteraction, true);
    document.addEventListener("visibilitychange", yieldToInteraction);
    return () => {
      disposed = true;
      stop();
      active?.abort();
      document.removeEventListener("pointerdown", yieldToInteraction, true);
      document.removeEventListener("keydown", yieldToInteraction, true);
      document.removeEventListener("visibilitychange", yieldToInteraction);
    };
  }, [
    params.enabled,
    params.wallet.address,
    params.wallet.signer,
    params.wallet.spendingKeys,
    pool,
  ]);
}
